import { get, set } from 'lodash';
import type { Reference } from '../reference/reference';

export const handleAndCommitChange = (values, id: string, value, validator, setValues, setValid) => {
    const newState = { ...values };
    set(newState, id, value);
    const validation = validator.validate(newState, id);
    setValues(newState);
    setValid({ errors: validation.errors, isValid: validation.isValid });
    return newState;
};

const serviceFieldnameMap = {
    'show.summary.headline': 'headline',
    'organization.summary.headline': 'headline'
};
export const processServiceErrors = (errors, valid, setValid, validator, fieldMap: Object = {}) => {
    errors.forEach((err) => {
        let field = serviceFieldnameMap[err.field.toLowerCase()];
        if (Object.keys(fieldMap).length > 0) {
            field = fieldMap[err.field.toLowerCase()];
        }
        if (field) {
            const errors = valid.errors;
            errors[field] = [err.message];
            validator.invalidate(field, err.message);
            setValid({ isValid: false, errors: errors });
        }
        console.log('Field invalid:', err.field, '=>', err.message);
    });
};

export const hasErrors = (valid, fieldName) => {
    return Array.isArray(get(valid.errors, fieldName)) && get(valid.errors, fieldName).length > 0;
};

export type ReferenceOption = {
    label: string;
    id?: string;
    subLabel?: string;
    /** Account email, when the option represents an account. */
    email?: string;
    isAdd?: boolean;
    ref?: Reference;
    createOption?: boolean;
    inActive?: boolean;
};

export const referenceToOption = (obj: Reference): ReferenceOption => {
    return {
        id: obj.id,
        label: obj.label,
        ref: obj
    };
};

export type EntityOption = {
    label: string;
};

/**
 * Transforms a generic entity object into an `EntityOption` for the Autocomplete.
 *
 * The returned option keeps the entity fields and guarantees a `label` required by MUI Autocomplete.
 * Label resolution follows this order: `reference.label` (if provided), then `entity.label`, then `entity.name`.
 *
 * @param entity - The source entity object to transform.
 * @param reference - Optional reference object used as the primary source of `label`.
 * @returns A new `EntityOption` object containing entity fields with a resolved label.
 */
export const entityToOption = (entity: Record<string, any>, reference?: Reference): EntityOption => {
    const option = {
        ...(entity || {}),
        label: reference?.label || entity?.label || entity?.name || ''
    };
    return option;
};

export const optionToEntity = (entity: Record<string, any>, multiple: boolean) => {
    if (multiple && Array.isArray(entity)) {
        return entity.map((item) => ({ ...item }));
    }
    return { ...entity };
};

export const optionToReference = (
    option: ReferenceOption | Reference | Array<Reference> | Array<ReferenceOption> | undefined,
    multiple: boolean
): Reference[] | Reference | undefined => {
    if (multiple) {
        if (!Array.isArray(option)) return [];
        const refs = option
            .map((item) => {
                if (!item) return undefined;
                if ('createOption' in item && (item as ReferenceOption).createOption) return undefined;
                if ('isAdd' in item && (item as ReferenceOption).isAdd) return undefined;
                if ('inActive' in item && (item as ReferenceOption).inActive) return undefined;
                return 'ref' in item ? (item as ReferenceOption).ref : (item as Reference);
            })
            .filter((r): r is Reference => !!r);
        return refs;
    }

    if (!option) return undefined;

    if (Array.isArray(option)) {
        const first = option[0];
        if (!first) return undefined;
        if ('createOption' in first && (first as ReferenceOption).createOption) return undefined;
        if ('isAdd' in first && (first as ReferenceOption).isAdd) return undefined;
        if ('inActive' in first && (first as ReferenceOption).inActive) return undefined;
        return 'ref' in first ? (first as ReferenceOption).ref : (first as Reference);
    }

    if ('ref' in option) {
        const opt = option as ReferenceOption;
        if (opt.createOption || opt.isAdd || opt.inActive) return undefined;
        return opt.ref;
    }

    return option as Reference;
};

export const isReferenceOptionEqualToValue = (option: ReferenceOption, value) => {
    const valueId = value ? (value.reference ? value.reference.id : value.id) : undefined;
    return option && valueId ? option.id === valueId : false;
};
